import {ChangeEvent, FC, useCallback, useMemo} from 'react';
import {useModal} from 'react-modal-hook';
import heic2any from 'heic2any';
import {MediaUploadModal} from '~/common/components/MediaUploadModal/MediaUploadModal';
import {useCachedMedia} from '~/common/hooks/useCachedMedia';
import {StickerUploadIcon} from './StickerUploadIcon';
import {UYO_STICKER_UPLOAD_PREFIX} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Sticker/components/Stickers/constants';
import {STICKER_UPLOAD_INDEXED_DB_STORE_NAME, StoredData} from '~/common/indexed-db/dexieStores';
import './StickerUpload.sass';

const MAX_UPLOAD_SIZE_LIMIT = 1024 * 5; // 5MB
const ACCEPTED_FILE_MINE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/gif',
];
interface StickerListProps {
  onClickHandler: (icon: StoredData, id: string) => void;
}
export const StickerUpload: FC<StickerListProps> = ({onClickHandler}) => {
  const {storeAndUpdateCachedMediaById} = useCachedMedia(STICKER_UPLOAD_INDEXED_DB_STORE_NAME);

  const [showModal, hideModal] = useModal(() => (
    <MediaUploadModal
      hideModal={hideModal}
      header="Your file could not be uploaded."
      description="Looks like your file is too large. Make sure your file is less than 5MB, then try again."
      ctaButtonText="Got it"
      appElement="#fabric-create-invitation-root"
    />
  ));

  const [showFileExtModal, hideFileExtModal] = useModal(() => (
    <MediaUploadModal
      hideModal={hideFileExtModal}
      header="Your file could not be uploaded."
      description="Looks like we don't accept that file type. Please try again with a PNG, JPEG, or HEIC."
      ctaButtonText="Got it"
      appElement="#fabric-create-invitation-root"
    />
  ));

  const onImageChange = useCallback(
    async (fileEvent: ChangeEvent<HTMLInputElement>) => {
      if (!fileEvent?.target?.files) return;

      let currentFile = fileEvent.target?.files[0];
      if (!currentFile) return;

      if (!ACCEPTED_FILE_MINE_TYPES.includes(currentFile.type)) {
        showFileExtModal();
        return;
      }

      const reader = new FileReader();

      if (Math.round(currentFile.size / 1024) >= MAX_UPLOAD_SIZE_LIMIT) {
        showModal();

        // this enables the same file to be revalidate if uploaded
        // eslint-disable-next-line
        fileEvent.target.value = '';
        return;
      }

      if (currentFile.type === 'image/heic') {
        // fabric doesn't allow heic file types so...
        // convert heic to jpeg
        try {
          const blob = await heic2any({blob: currentFile, toType: 'image/jpeg', quality: 0.9});
          const jpegFile = new File([blob] as unknown as BlobPart[], 'image.jpeg', {
            type: 'image/jpeg',
          });
          currentFile = jpegFile;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Conversion failed:', error);
          return;
        }
      }

      reader.addEventListener('load', async () => {
        const result = reader.result as string;
        const id = `${UYO_STICKER_UPLOAD_PREFIX}${crypto.randomUUID()}`;
        const newMedia = await storeAndUpdateCachedMediaById(result, id);

        // this enables the same file to be revalidate if uploaded
        // eslint-disable-next-line
        fileEvent.target.value = '';
        if (newMedia) {
          onClickHandler(newMedia, newMedia.id);
        }
      });

      reader.onerror = (evt: ProgressEvent) => {
        // eslint-disable-next-line no-console
        console.error('file onerror: ', evt);
      };

      reader.readAsDataURL(currentFile);
    },
    [onClickHandler, showFileExtModal, showModal, storeAndUpdateCachedMediaById]
  );

  return useMemo(
    () => (
      <>
        <label htmlFor="sticker-upload" className="sticker-upload-btn">
          <StickerUploadIcon />
        </label>
        <input
          type="file"
          id="sticker-upload"
          name="sticker-upload"
          accept={ACCEPTED_FILE_MINE_TYPES.join(',')}
          style={{display: 'none'}}
          onChange={(fileEvent) => onImageChange(fileEvent)}
          data-qa-id="sticker-upload-btn"
        />
      </>
    ),
    [onImageChange]
  );
};
